import { isProduction } from "@/config/config";

/**
 * Stable default landing target: the workspace root, which renders Overview
 * (#592/doc-08 §3.1). Referenced by the workspace-layout inaccessible-workspace
 * redirect. Superseded #496's adaptive Publish-vs-Emails heuristic — the v2 IA
 * has one static landing surface, not two candidates to pick between.
 */
export const DASHBOARD_HOME = "/dashboard/w/$workspaceId" as const;

/** Local Next docs dev server; production serves `/docs/` from the same origin. */
export function docsHref(): string {
  return import.meta.env.DEV ? "//localhost:3030/docs/" : "/docs/";
}

/** The product guides on the docs site that the dashboard links to (#1429). */
export type DocsGuide = "analytics" | "search-console";

/**
 * A guide page under `docsHref()`, with an optional `#fragment` — built off
 * `docsHref()` so dev keeps pointing at the Next dev server and production stays
 * same-origin `/docs/`.
 */
export function docsGuideHref(guide: DocsGuide, fragment?: string): string {
  return `${docsHref()}${guide}/${fragment ? `#${fragment}` : ""}`;
}

/**
 * Marketing links go directly to the company blog after deployment. Local Vite
 * keeps the app-relative URL so development never depends on a deployed host.
 * The app-host `/blog/*` redirect remains for old inbound URLs; this helper
 * deliberately does not participate in that server-side compatibility path.
 */
export function blogHref(): string {
  if (import.meta.env.DEV) return "/blog";
  return isProduction
    ? "https://blog.goodsender.com"
    : "https://blog.dev.goodsender.com";
}

/**
 * The app host does not expose a subscription endpoint. React's fallback must
 * therefore link directly to the company blog's native inline form rather than
 * using local `/blog/#subscribe` (which can remain app-hosted in local Vite).
 */
export function companyBlogSubscribeHref(): string {
  return isProduction
    ? "https://blog.goodsender.com/#subscribe"
    : "https://blog.dev.goodsender.com/#subscribe";
}
